import { Post, SOCIAL_NETWORK_POST_STATUS_NAMES, SocialNetworkPosted, SocialNetworkPostStatus } from "@/interfaces";
import * as React from "react";
import { Checkbox, Table } from "semantic-ui-react";
import {
  formatDate,
  getUrlForPlayer,
  getUrlForPrompt,
  getUrlForSocialNetwork,
  wrapInLinkIfHrefIsPresent,
} from "./utils";
import { MediaPlayer } from "@/components/MediaPlayer";
import { SocialNetworkIcon } from "@/components/SocialNetworkIcon";
import { useEffect } from "react";
import { TableRecord } from "./PostsTable";
import { PostServices, PostStatsResponse } from "@/services/bedrock";
import { groupBy } from "lodash";
import { Badge } from "@/components/ui";
import { useSocialCoachSession } from "@/utils";

const STATUS_BADGE_STYLES: Record<SocialNetworkPostStatus, { className: string }> = {
  DELETED: { className: "bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100" },
  FAILED: { className: "bg-red-50 text-red-600 border-red-100 hover:bg-red-100" },
  FAILURE_ACKED: { className: "bg-orange-50 text-orange-600 border-orange-100 hover:bg-orange-100" },
  IN_TRANSIT: { className: "bg-blue-50 text-blue-600 border-blue-100 hover:bg-blue-100 animate-pulse" },
  PENDING: { className: "bg-blue-50 text-blue-700 border-blue-100 hover:bg-blue-100" },
  POSTED: { className: "bg-emerald-50 text-emerald-600 border-emerald-100 hover:bg-emerald-100" },
  SKIPPED_NO_CONNECTION: { className: "bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100" },
};

type Props = {
  record: TableRecord;
  canSelect: boolean;
  setSelectedPosts: (value: ((prevState: number[]) => number[]) | number[]) => void;
  handlePost: (post: Post, socialPost?: SocialNetworkPosted) => void;
};

export const PostBody: React.FC<Props> = ({ record, canSelect, setSelectedPosts, handlePost }) => {
  const { isPlayer, session } = useSocialCoachSession();
  const { isSelected, post, prompt, player, socialNetworkPosts } = record;
  const showTotal = socialNetworkPosts.length > 1;
  const rowSpan = socialNetworkPosts.length + (showTotal ? 1 : 0);

  const [postStats, setPostStats] = React.useState<PostStatsResponse>();

  const requestPostStats = async () => {
    if (post.postId) {
      const stats = await PostServices.getPostStats(post.postId);
      setPostStats(stats);
    }
  };

  const statsByNetwork = groupBy(postStats?.networkBreakdown || [], n => n.socialNetworkId);

  useEffect(() => {
    requestPostStats().catch(console.error);
  }, [post]);

  const onSelected = () => {
    if (!post.canBeUpdatedOrDeleted) return;
    setSelectedPosts((current: number[]) => {
      return isSelected ? current.filter(postId => postId !== post.postId) : [...new Set([...current, post.postId])];
    });
  };

  return (
    <Table.Body
      key={`post-row-${record.post.postId}`}
      className={post.canBeUpdatedOrDeleted ? "selectablePost" : ""}
      onClick={() => handlePost(post)}
    >
      {socialNetworkPosts.map((socialNetworkPost, index) => {
        const media = post.mediaGroup?.media?.[0];
        const socialNetworkUrl = getUrlForSocialNetwork(socialNetworkPost);
        const stat = statsByNetwork[socialNetworkPost.socialNetworkId]?.[0];
        const failed = socialNetworkPost.status === "FAILED" || socialNetworkPost.status === "FAILURE_ACKED";
        const [date, ...time] = formatDate(socialNetworkPost.postedTimestamp).toString().split(" ");

        return (
          <Table.Row active={isSelected} key={`row-${index}`}>
            {index === 0 && (
              <>
                {canSelect && (
                  <Table.Cell rowSpan={rowSpan} textAlign="center">
                    {post.canBeUpdatedOrDeleted && (
                      <Checkbox
                        checked={isSelected}
                        disabled={!post.canBeUpdatedOrDeleted}
                        onChange={onSelected}
                        onClick={e => e.stopPropagation()}
                      />
                    )}
                  </Table.Cell>
                )}
                {isPlayer || (
                  <Table.Cell rowSpan={rowSpan}>
                    {wrapInLinkIfHrefIsPresent(
                      getUrlForPlayer(session, player.playerId),
                      <>
                        <div className="boldText">{player.email}</div>
                        <div>
                          {player.firstName} {player.lastName}
                        </div>
                      </>
                    )}
                  </Table.Cell>
                )}
                <Table.Cell rowSpan={rowSpan} textAlign="center">
                  {media && (
                    <div className="promptContainer">
                      <MediaPlayer media={media} />
                      {prompt && (
                        <div className="promptTitle">
                          {wrapInLinkIfHrefIsPresent(getUrlForPrompt(session, prompt.promptId), prompt.title)}
                        </div>
                      )}
                    </div>
                  )}
                </Table.Cell>
              </>
            )}
            <Table.Cell textAlign="left">
              <div className="socialNetworkIconColumn">
                {wrapInLinkIfHrefIsPresent(
                  socialNetworkUrl,
                  <SocialNetworkIcon socialNetworkId={socialNetworkPost.socialNetworkId} size="small" />
                )}
              </div>
            </Table.Cell>
            <Table.Cell>
              <Badge
                variant="outline"
                className={`${
                  STATUS_BADGE_STYLES[socialNetworkPost.status].className
                } cursor-pointer whitespace-nowrap`}
                onClick={e => {
                  e.stopPropagation();
                  handlePost(post, socialNetworkPost);
                }}
              >
                {SOCIAL_NETWORK_POST_STATUS_NAMES[socialNetworkPost.status]}
              </Badge>
            </Table.Cell>
            <Table.Cell>
              {socialNetworkPost.status === "POSTED" && (
                <>
                  <div>{date}</div>
                  <div>{time.join(" ")}</div>
                </>
              )}
            </Table.Cell>
            <Table.Cell>{stat?.latestStats?.impressionCount}</Table.Cell>
            <Table.Cell>{stat?.latestStats?.totalReactions}</Table.Cell>
            <Table.Cell>{stat?.latestStats?.commentCount}</Table.Cell>
            <Table.Cell>{stat?.latestStats?.shareCount}</Table.Cell>
          </Table.Row>
        );
      })}
      {showTotal && (
        <Table.Row active={isSelected} key={`row-total`}>
          <Table.Cell colSpan={3} textAlign={"right"}>
            <b>Totals</b>
          </Table.Cell>
          <Table.Cell>{postStats?.totals?.impressionCount}</Table.Cell>
          <Table.Cell>{postStats?.totals?.totalReactions}</Table.Cell>
          <Table.Cell>{postStats?.totals?.commentCount}</Table.Cell>
          <Table.Cell>{postStats?.totals?.shareCount}</Table.Cell>
        </Table.Row>
      )}
    </Table.Body>
  );
};
